import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Button, FormControl, TextField } from '@mui/material';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { nanoid } from 'nanoid';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import SaveIcon from '@mui/icons-material/Save';
import ModalWindow from '../ModalWindow/ModalWindow';
import { DashboardContext } from '../DashboardContext';
import { localizeErrors } from '../../../utils/helpers';

export const AddExpensesCategory = ({ open, handleClose, selectedCategory, editMode, expense = true }) => {
  const {
    isFetchingExpenses, expensesErrors,
    fetchAddExpensesCategory, fetchEditExpensesCategory,
    isFetchingArrivals, addArrivalCategory, editArrivalsCategory, arrivalsErrors
  } = useContext(DashboardContext);

  const submitForm = useCallback((value, resetForm) => {
    const { title } = value;
    if (expense) {
      if (editMode) {
        fetchEditExpensesCategory({ id: selectedCategory.id, title }).then((resp) => {
          if (resp === 'success') {
            handleClose();
            resetForm();
          }
        });
      } else {
        fetchAddExpensesCategory({ title }).then((resp) => {
          if (resp === 'success') {
            handleClose();
            resetForm();
          }
        });
      }
    } else if (editMode) {
      editArrivalsCategory({ id: selectedCategory.id, title }).then((resp) => {
        if (resp === 'success') {
          handleClose();
          resetForm();
        }
      });
    } else {
      addArrivalCategory({ title }).then((resp) => {
        if (resp === 'success') {
          handleClose();
          resetForm();
        }
      });
    }
  }, [expense, editMode, fetchEditExpensesCategory, selectedCategory,
    handleClose, fetchAddExpensesCategory, addArrivalCategory, editArrivalsCategory]);

  const formik = useFormik({
    initialValues: {
      title: editMode ? selectedCategory.title : ''
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      title: yup
        .string()
        .max(48, 'Максимально 48 символів')
        .required('Обов\'язково до заповнення')
    }),
    onSubmit: (values, { resetForm }) => {
      submitForm(values, resetForm);
    }
  });

  const onClickReset = () => {
    formik.resetForm();
    handleClose();
  };

  const renderErrors = useMemo(() => {
    const errors = expense ? expensesErrors : arrivalsErrors;
    return errors?.map((item) => <p key={nanoid()} className="errorText">{localizeErrors[item] || item}</p>);
  }, [expense, expensesErrors, arrivalsErrors]);

  return (
    <ModalWindow
      open={open}
      maxWidth="700px"
      onClose={handleClose}
      onBackdropClick={onClickReset}
    >
      <div className="addUsers addPopup">
        <form onSubmit={formik.handleSubmit}>
          <FormControl size="small" fullWidth>
            <TextField
              size="small"
              id="title"
              label="Назва категорії *"
              name="title"
              placeholder="Введіть назву категорії"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />
            {renderErrors}
          </FormControl>
          <div className="addUsers-actions">
            <LoadingButton
              variant="contained"
              loading={expense ? isFetchingExpenses : isFetchingArrivals}
              type="submit"
              disabled={!formik.isValid}
            >
              <SaveIcon sx={{ marginRight: '5px' }} />
              Зберегти
            </LoadingButton>
            <Button type="reset" variant="contained" color="error" onClick={() => onClickReset()}>
              <DisabledByDefaultIcon sx={{ marginRight: '5px' }} />
              Скасувати
            </Button>
          </div>
        </form>
      </div>
    </ModalWindow>
  );
};

AddExpensesCategory.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  selectedCategory: PropTypes.object,
  editMode: PropTypes.bool,
  expense: PropTypes.bool
};
